import { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export type ScoreTone = 'good' | 'watch' | 'bad' | 'neutral';

const STROKE: Record<ScoreTone, string> = {
  good: 'stroke-success',
  watch: 'stroke-warning',
  bad: 'stroke-destructive',
  neutral: 'stroke-foreground',
};

/**
 * 0–100 read the usual way: 70 and up is healthy, under 40 needs work.
 * `invert` for scores where high is bad (difficulty, spam).
 */
export function scoreTone(value: number | null | undefined, { good = 70, bad = 40, invert = false } = {}): ScoreTone {
  if (value == null) return 'neutral';
  if (invert) return value >= good ? 'bad' : value < bad ? 'good' : 'watch';
  return value >= good ? 'good' : value < bad ? 'bad' : 'watch';
}

/**
 * A 0–100 score as a ring: site health, authority, how hard a search is.
 * The number is the point; the ring says how far it is from full.
 */
export function ScoreRing({
  value,
  max = 100,
  size = 96,
  tone,
  label,
  sublabel,
  className,
  testId,
}: {
  value: number | null | undefined;
  max?: number;
  size?: number;
  /** Defaults to reading `value` with `scoreTone`. */
  tone?: ScoreTone;
  label?: ReactNode;
  sublabel?: ReactNode;
  className?: string;
  testId?: string;
}) {
  const stroke = Math.max(5, Math.round(size / 12));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = value == null ? 0 : Math.max(0, Math.min(1, value / max));
  const t = tone ?? scoreTone(value == null ? null : (value / max) * 100);

  return (
    <div className={cn('flex items-center gap-4', className)} data-testid={testId}>
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="stroke-muted" />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={c * (1 - pct)}
            className={cn(STROKE[t], 'transition-[stroke-dashoffset] duration-700')}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-heading font-medium tabular-nums" style={{ fontSize: Math.round(size * 0.28) }}>
          {value == null ? '—' : Math.round(value)}
        </span>
      </div>
      {(label || sublabel) && (
        <div className="min-w-0">
          {label && <p className="text-sm font-medium">{label}</p>}
          {sublabel && <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{sublabel}</p>}
        </div>
      )}
    </div>
  );
}
